import "./style.css";
import { useEffect, useState, useCallback } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AgGridReact } from "ag-grid-react";
import { Link } from "react-router-dom";
import {
  commonGetData,
  commonSaveData,
  initState,
  initCommonState,
} from "../../../redux/slices/commonSlice";
import {
  getEmployeeAttendanceListPath,
  deactivateEmployeePath,
} from "../../../services/config";
import Widgets from "../../../components/Widgets";
import debounce from "lodash/debounce";
import { Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import { FiDownload } from "react-icons/fi";
import { PiPencilSimpleLineBold } from "react-icons/pi";
import CustomPagination from "../../../components/CustomPagination";
import {
  getCurrentDate,
  getInitials,
  getInitialColor,
} from "../../../services/commonServices";
import { HasPermission } from "../../../services/hasPermission";
import TableSkeleton from "../../../components/table/TableSkeleton.js";
import { MdClear } from 'react-icons/md';
import { useGlobalToast } from '../../../stopOldToastGlobally.js';
import * as XLSX from "xlsx";


// ============================================================
// ATTENDANCE DOWNLOAD HEADERS (exact order from document)
// ============================================================
const ATTENDANCE_HEADERS = [
  "Sl No",
  "Employee ID",
  "Employee Name",
  "Department",
  "Designation",
  "Branch",
  "Payroll Cycle",
  "Attendance Month",
  "Total Calendar Days",
  "1", "2", "3", "4", "5", "6", "7",
  "8", "9", "10", "11", "12", "13", "14",
  "15", "16", "17", "18", "19", "20", "21",
  "22", "23", "24", "25", "26", "27", "28",
  "29", "30", "31",
];

// Map raw employee row → flat object matching ATTENDANCE_HEADERS
const mapAttendanceRow = (item, index) => {
  const base = {
    "Sl No": index + 1,
    "Employee ID": item.employeeId || item.employee_id || "-",
    "Employee Name": item.name || item.full_name || "-",
    "Department": item.departmentName || item.department || "-",
    "Designation": item.job_title || item.designation || "-",
    "Branch": item.branchName || item.branch || "-",
    "Payroll Cycle": item.payroll_cycle || "-",
    "Attendance Month": item.attendance_month || "-",
    "Total Calendar Days": item.total_days || "-",
  };
  // Daily attendance: supports array, day_N keys, or direct number keys
  for (let d = 1; d <= 31; d++) {
    base[String(d)] =
      item.daily_attendance?.[d - 1] ??
      item[`day_${d}`] ??
      item[`${d}`] ??
      "-";
  }
  return base;
};
// Excel column widths
const ATTENDANCE_COL_WIDTHS = [
  { wch: 6 }, { wch: 14 }, { wch: 24 }, { wch: 18 }, { wch: 20 },
  { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
  ...Array(31).fill({ wch: 4 }),
];

// CSV builder
const buildCSV = (rows) => {
  const escape = (val) => {
    const str = String(val ?? "-");
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  return [
    ATTENDANCE_HEADERS.map(escape).join(","),
    ...rows.map((row) => ATTENDANCE_HEADERS.map((h) => escape(row[h])).join(",")),
  ].join("\n");
};

const AttendanceList = ({
  dispatch,
  user,
  state,
  branchList,
  designationList,
  departmentList
}) => {
 const customToast = useGlobalToast();
  const [usersList, setUsersList] = useState([]);
  const [filteredUsersList, setFilteredUsersList] = useState([]);
  const [filterUser, setFilterUser] = useState("");

  const [jobTitle, setJobTitle] = useState("");
  const [attendanceType, setAttendanceType] = useState("");
  const [branch, setBranch] = useState(0);
  const [departmentId, setDepartmentId] = useState(0);

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const path = `${getEmployeeAttendanceListPath}${user.user_id
    }&branchId=${branch}&departmentId=${departmentId}&jobTitle=${jobTitle}`;

  const getEmployeeList = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    dispatch(commonGetData(path));
  };

  useEffect(() => {
    getEmployeeList();
  }, [jobTitle, attendanceType, branch, departmentId, dispatch, path]);

  useEffect(() => {
    if (state.commonGetSuccess) {
      if (state.commonGetData == undefined || state.commonGetData == "") {
        setUsersList([]);
        setFilteredUsersList([]);
        dispatch(initState());
      } else {
        if (filterUser) {
          handleSearch(filterUser, state.commonGetData);
        } else {
          if (state.commonGetData && state.commonGetData[0]?.name) {
            setUsersList(state.commonGetData);
            setFilteredUsersList(state.commonGetData);
            dispatch(initState());
          }
        }
      }
    }

    if (state.commonGetError) {
      setUsersList([]);
      setFilteredUsersList([]);
      dispatch(initState());
    }

    if (state.commonSuccess) {
      getEmployeeList();
      dispatch(initCommonState());
    }
    if (state.commonError) {
      dispatch(initCommonState());
    }
  }, [state]);

  const handleActiveInactiveChange = (event, rowData) => {
    const data = {
      path: deactivateEmployeePath,
      data: {
        userId: user.user_id,
        staffId: rowData.staffId,
        isActiveUser: event.target.value,
      },
    };
    dispatch(commonSaveData(data));
  };

  const columns = [
    {
      headerName: "S.No",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 70,
      pinned: "left",
    },
    {
      headerName: "Name",
      pinned: "left",
      width: 250,
      cellRenderer: NameCell
    },
    {
      headerName: "Job Title",
      field: "job_title",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Schedule Type",
      field: "schedule_type",
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => params.data?.schedule_type || "-",
    },
    {
      headerName: "Shift Name",
      field: "shiftName",
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => params.data?.shiftName || "-",
    },
    {
      headerName: "Status",
      field: "status",
      width: 100,
      cellRenderer: (rowData) => {
        return (
          <select
            className={rowData.data.status == "1" ? "statusGreen" : "statusOrange"}
            value={rowData.data.status}
            onChange={(e) => handleActiveInactiveChange(e, rowData.data)}
            style={{ width: "90px", fontSize: "12px", padding: "4px" }}
          >
            <option value="1">Active</option>
            <option value="0">InActive</option>
          </select>
        );
      },
    },
    {
      headerName: "Action",
      pinned: "right",
      width: 80,
      cellRenderer: (rowData) => {
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Link
              to={`user-info/${rowData.data.staffId}`}
              className="table-link"
              style={{ fontSize: "20px" }}
            >
              <PiPencilSimpleLineBold className="edit-icon" />
            </Link>
          </div>
        );
      },
    },
  ];

  const handleSearch = useCallback(
    debounce((query, dataList) => {
      const debouncedQuery = query.trim().toLowerCase();
      const results = dataList.filter(
        (employee) =>
          (employee.name &&
            employee.name.toLowerCase().includes(debouncedQuery)) ||
          (employee.schedule_type &&
            employee.schedule_type.toLowerCase().includes(debouncedQuery)) ||
          (employee.shiftName &&
            employee.shiftName.toLowerCase().includes(debouncedQuery)) ||
          (employee.job_title &&
            employee.job_title.toLowerCase().includes(debouncedQuery)) ||
          (employee.employeeId &&
            employee.employeeId.toLowerCase().includes(debouncedQuery))
      );
      setFilteredUsersList(results);
      setUsersList(dataList);
    }, 500),
    [usersList]
  );

  const searchUser = (event) => {
    const { target: { value } } = event;
    setFilterUser(value);
    handleSearch(value, usersList);
  };

  // ── CSV Download ──
  const downloadCSVFile = (data) => {
    const rows = data.map((item, idx) => mapAttendanceRow(item, idx));
    const csv = buildCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Attendance_Details_${getCurrentDate()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    customToast.success("CSV downloaded successfully!");
  };

  // ── Excel Download ──
  const downloadExcel = (data) => {
    const rows = data.map((item, idx) => mapAttendanceRow(item, idx));
    const wsData = [
      ATTENDANCE_HEADERS,
      ...rows.map((row) => ATTENDANCE_HEADERS.map((h) => row[h] ?? "-")),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = ATTENDANCE_COL_WIDTHS;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Details");
    XLSX.writeFile(wb, `Attendance_Details_${getCurrentDate()}.xlsx`);
    customToast.success("Excel downloaded successfully!");
  };

  // ── Main download handler (replaces old downloadBulkData) ──
  const downloadBulkData = () => {
    if (!filteredUsersList || filteredUsersList.length === 0) {
      customToast.warning("No data to export");
      return;
    }
    // Default to CSV (same as original behaviour)
    downloadCSVFile(filteredUsersList);
  };

  const paginationPageSize = 10;

  const clearFilter = () => {
    setUsersList([]);
    setFilteredUsersList([]);
    getEmployeeList();
    setFilterUser("");
    setJobTitle("");
    setDepartmentId(0);
    setBranch(0);
  };

  return (
    <div>
      <div className="search-box flex-space-between mt-m-40">
        <Form className="d-flex align-items-center justify-content-start gap-2">
          <Form.Group>
            <Form.Control
              placeholder="Search by Employee Name & ID"
              onChange={searchUser}
              name="filterUser"
              value={filterUser}
              className="universal-input-one"
              style={{ width: "250px" }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setJobTitle(e.target.value)}
              name="jobTitle"
              value={jobTitle}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={""}>Select Job Title</option>
              {designationList?.map((row) => {
                return <option value={encodeURIComponent(row.label)} key={row.label}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setBranch(e.target.value)}
              name="branch"
              value={branch}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Branch</option>
              {branchList?.map((row) => {
                return <option value={row.branchId} key={row.branchId}>{row.branch_name}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setDepartmentId(e.target.value)}
              name="departmentId"
              value={departmentId}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value="0">Select Department</option>
              {departmentList?.map((row) => {
                return <option value={row.value} key={row.value}>{row.label}</option>;
              })}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Select
              onChange={(e) => setAttendanceType(e.target.value)}
              name="attendanceType"
              value={attendanceType}
              className="universal-input-one"
              style={{ width: "200px" }}
            >
              <option value={0}>Select Attendance Type</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            {
              (filterUser ||
                jobTitle ||
                departmentId != 0 ||
                branch != 0) && <button type="button" className="clear" onClick={clearFilter}>
                <MdClear /> Clear
              </button>
            }
          </Form.Group>
        </Form>
      </div>

      <Widgets
        body={
          <>
            <div
              className="ag-theme-quartz custom-grid"
              style={{ height: "calc(100vh - 310px)", width: "100%" }}
            >
              {state.commonGetLoading ? (
                <TableSkeleton />
              ) : (
                <AgGridReact
                  rowData={Array.isArray(filteredUsersList) ? filteredUsersList : []}
                  columnDefs={columns}
                  domLayout="auto-height"
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  onGridReady={onGridReady}
                />
              )}
            </div>
            <div className="d-flex align-items-center justify-content-between custom-pagination">
              {gridApi && <CustomPagination gridApi={gridApi} />}
              {HasPermission("Attendance Details", "export") && (
                <Button
                  onClick={downloadBulkData}
                  className="bulkDataDownloads universal-btn"
                >
                  <FiDownload style={{ fontSize: "16px", marginRight: '10px' }} />
                  Export Data
                </Button>
              )}
            </div>
          </>
        }
      />
    </div>
  );
};

const NameCell = (props) => {
  const { data } = props;
  return (
    data?.staffId ?
      <Link
        to={`user-info/${data.staffId}`}
        className="table-link d-flex align-items-center justify-content-start gap-2"
      >
        <div
          style={{
            width: "30px",
            height: "30px",
            border: "1px solid lightgray",
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: getInitialColor(data.name),
            color: "white",
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          {getInitials(data.name)}
        </div>
        {data.name}
      </Link> : "-"
  );
};

export default AttendanceList;